import { DTO } from '@/types/dto-wrapper';
import {
  GetTransactionDTO,
  TransactionType,
  TransactionStatus,
  TransactionSource,
  TransactionLocation,
  TransactionUser,
} from '../api/transactions/transaction.contracts';

// Helper function to get styling for each transaction type
export const getTransactionTypeStyle = (type: TransactionType) => {
  switch (type) {
    case TransactionType.Transfer:
      return {
        bg: 'bg-blue-100 dark:bg-blue-500/20',
        text: 'text-blue-800 dark:text-blue-300',
      };
    case TransactionType.Correction:
      return {
        bg: 'bg-red-100 dark:bg-red-500/20',
        text: 'text-red-800 dark:text-red-300',
      };
    case TransactionType.System:
      return {
        bg: 'bg-purple-100 dark:bg-purple-500/20',
        text: 'text-purple-800 dark:text-purple-300',
      };
    default:
      return {
        bg: 'bg-gray-100 dark:bg-gray-500/20',
        text: 'text-gray-800 dark:text-gray-300',
      };
  }
};

// Helper function to get styling for each transaction status
export const getTransactionStatusStyle = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.InProgress:
      return {
        bg: 'bg-yellow-100 dark:bg-yellow-500/20',
        text: 'text-yellow-800 dark:text-yellow-300',
      };
    case TransactionStatus.Finalized:
      return {
        bg: 'bg-green-100 dark:bg-green-500/20',
        text: 'text-green-800 dark:text-green-300',
      };
    default:
      return {
        bg: 'bg-gray-100 dark:bg-gray-500/20',
        text: 'text-gray-800 dark:text-gray-300',
      };
  }
};

// Helper function to get styling for each transaction source
export const getTransactionSourceStyle = (source: TransactionSource) => {
  switch (source) {
    case TransactionSource.Manual:
      return {
        bg: 'bg-indigo-100 dark:bg-indigo-500/20',
        text: 'text-indigo-800 dark:text-indigo-300',
      };
    case TransactionSource.Automatic:
      return {
        bg: 'bg-cyan-100 dark:bg-cyan-500/20',
        text: 'text-cyan-800 dark:text-cyan-300',
      };
    default:
      return {
        bg: 'bg-gray-100 dark:bg-gray-500/20',
        text: 'text-gray-800 dark:text-gray-300',
      };
  }
};

export class TransactionModel extends DTO<GetTransactionDTO> {
  constructor(dto: GetTransactionDTO) {
    super(dto);
  }

  get id(): number {
    return this.dto.id;
  }

  get transactionId(): string {
    return this.dto.transactionId;
  }

  get type(): TransactionType {
    return this.dto.type;
  }

  get status(): TransactionStatus {
    return this.dto.status;
  }

  get source(): TransactionSource {
    return this.dto.source;
  }

  get toLocation(): TransactionLocation {
    return this.dto.toLocation;
  }

  get fromLocation(): TransactionLocation {
    return this.dto.fromLocation;
  }

  get toLocationId(): number {
    return this.dto.toLocation.id;
  }

  get fromLocationId(): number {
    return this.dto.fromLocation.id;
  }

  get carrier(): TransactionLocation | undefined {
    return this.dto.carrier;
  }

  get carrierId(): number | undefined {
    return this.dto.carrier?.id;
  }

  get date(): Date | undefined {
    return this.dto.transactionDate ? new Date(this.dto.transactionDate) : undefined;
  }

  get documentNumber(): string | undefined {
    return this.dto.documentNumber;
  }

  get referenceNumber(): string | undefined {
    return this.dto.referenceNumber;
  }

  get notes(): string | undefined {
    return this.dto.notes;
  }

  get details(): GetTransactionDTO['details'] {
    return this.dto.details;
  }

  get createdAt(): Date {
    return new Date(this.dto.createdAt);
  }

  get updatedAt(): Date {
    return new Date(this.dto.updatedAt);
  }

  get deletedAt(): Date | undefined {
    return this.dto.deletedAt ? new Date(this.dto.deletedAt) : undefined;
  }

  get createdBy(): TransactionUser | undefined {
    return this.dto.createdBy;
  }

  static fromDTO(dto: GetTransactionDTO): TransactionModel {
    return new TransactionModel(dto);
  }

  toDTO(): GetTransactionDTO {
    return this.dto;
  }
}
